<script lang="ts">
	import { untrack, onDestroy } from 'svelte';
	import { fade } from 'svelte/transition';
	import { flip } from 'svelte/animate';
	import type { Coop } from '$lib/coop.svelte';
	import { clearActiveCoopRoom } from '$lib/coop.svelte';
	import { sound } from '$lib/sound';
	import { portal } from '$lib/actions';
	import Avatar from './Avatar.svelte';
	import Icon from '@iconify/svelte';

	// `badges` = player avatars (shown at the start of the nav row).
	// `actions` = skip + leave buttons (shown at the end of the nav row).
	let { coop, roomId: _roomId, part = 'badges' }: { coop: Coop; roomId: string; part?: 'badges' | 'actions' } = $props();

	const active = $derived(coop.activePlayer);
	const myTurn = $derived(coop.isMyTurn);
	const ready = $derived(coop.ready);
	const running = $derived(ready && !coop.state.finished);

	// During play, float the active player's avatar to the front of the stack.
	const orderedPlayers = $derived.by(() => {
		const players = coop.state.players;
		if (!running || !active) return players;
		return [active, ...players.filter((p) => p.id !== active.id)];
	});

	const canSkip = $derived(myTurn && running);

	// Brief full-screen flash overlay (e.g. "YOUR TURN", "TURN SKIPPED") — a cue
	// that's hard to miss without reading the nav. Runs in the `badges` instance
	// only so each flash fires exactly once. Flashes queue so a skip that passes
	// the turn to you shows "Turn skipped" and then "Your turn" in sequence.
	let flashText = $state<string | null>(null);
	let flashColor = $state('var(--correct-frame-fill)');
	let flashQueue: Array<{ text: string; color: string; sound: boolean }> = [];
	let flashActive = false;
	let flashTimer: ReturnType<typeof setTimeout> | null = null;

	const FLASH_COLORS = {
		correct: 'var(--correct-frame-fill)',
		absent: 'var(--absent-frame-fill)',
		neutral: 'var(--top-nav-frame-text)'
	};

	function pumpFlash() {
		const next = flashQueue.shift();
		if (!next) {
			flashActive = false;
			flashText = null;
			return;
		}
		flashText = next.text;
		flashColor = next.color;
		if (next.sound) sound.yourTurn();
		flashTimer = setTimeout(() => {
			flashText = null; // fade out, then show the next after a short gap
			flashTimer = setTimeout(pumpFlash, 220);
		}, 1300);
	}

	function triggerFlash(text: string, tone: keyof typeof FLASH_COLORS = 'correct', withSound = false) {
		flashQueue.push({ text, color: FLASH_COLORS[tone], sound: withSound });
		if (!flashActive) {
			flashActive = true;
			pumpFlash();
		}
	}

	// Single turn-change watcher so the cues queue in a deterministic order:
	// a skip that lands on you shows "Turn skipped" first, then "Your turn".
	let prevTurn = -1;
	let prevMoves = -1;
	let prevMine = false;
	let synced = false;

	$effect(() => {
		if (part !== 'badges') return;
		const turn = coop.state.turnIndex;
		const moves = coop.state.moves.length;
		const playerCount = coop.state.players.length;
		const isRunning = coop.ready && !coop.state.finished && !coop.state.solo;
		const mine = isRunning && coop.isMyTurn;
		untrack(() => {
			if (!synced) {
				// First observation (e.g. game just started): announce your turn if
				// it's already yours, but never a phantom skip.
				synced = true;
				if (mine) triggerFlash('Your turn', 'correct', true);
			} else if (isRunning && playerCount > 1 && turn !== prevTurn) {
				// Turn advanced with no new move applied → it was a skip.
				if (moves === prevMoves) triggerFlash('Turn skipped', 'absent');
				if (mine && !prevMine) triggerFlash('Your turn', 'correct', true);
			}
			prevTurn = turn;
			prevMoves = moves;
			prevMine = mine;
		});
	});

	onDestroy(() => {
		if (flashTimer) clearTimeout(flashTimer);
	});

	function leave() {
		if (coop.state.gameType) clearActiveCoopRoom(coop.state.gameType);
		window.location.href = '/';
	}
</script>

{#if part === 'badges'}
	<!-- Player badges: stacked (overlapping) avatars. The active player's avatar
	     moves to the front (first + top z-index) on their turn. -->
	<div class="flex items-center" title="Players in game">
		{#each orderedPlayers as p, i (p.id)}
			<span class="relative block shrink-0 transition" animate:flip={{ duration: 250 }} style="margin-left: {i === 0 ? '0' : '-0.5rem'}; z-index: {orderedPlayers.length - i}; filter: {running && active?.id !== p.id ? 'grayscale(1)' : 'none'}; opacity: {!p.connected ? 0.35 : running && active?.id !== p.id ? 0.4 : 1};">
				<Avatar avatar={p.avatar} username={p.username || 'Player'} size={8} extraClass={running && active?.id === p.id ? 'coop-avatar coop-avatar-active' : 'coop-avatar'} title="{p.username || 'Player'}{p.id === coop.userId ? ' (You)' : ''}" />
			</span>
		{/each}
	</div>

	{#if flashText}
		<div use:portal class="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-black/70" transition:fade={{ duration: 200 }}>
			<span class="turn-flash text-center text-5xl leading-none font-bold uppercase" style="color: {flashColor};">{flashText}</span>
		</div>
	{/if}
{:else if part === 'actions'}
	{#if canSkip}
		<button type="button" onclick={() => coop.skip()} aria-label="Skip partner's turn" title="Skip partner's turn" class="pixel-frame relative block size-8 shrink-0 cursor-pointer">
			<div class="pixel-frame absolute inset-0 bg-(--nav-action-frame-outer)"></div>
			<div class="pixel-frame absolute inset-0.5 bg-(--nav-action-frame-highlight)"></div>
			<div class="pixel-frame absolute inset-1 bg-(--nav-action-frame-shadow)"></div>
			<div class="pixel-frame absolute inset-1.5 bg-(--nav-action-frame-fill)"></div>
			<div class="pixel-frame absolute inset-1.5 flex items-center justify-center text-(--nav-action-frame-text)">
				<Icon icon="mdi:skip-next" class="size-5" />
			</div>
		</button>
	{/if}

	<button type="button" onclick={leave} aria-label="Leave co-op" title="Leave co-op" class="pixel-frame relative block size-8 shrink-0 cursor-pointer">
		<div class="pixel-frame absolute inset-0 bg-(--nav-action-frame-outer)"></div>
		<div class="pixel-frame absolute inset-0.5 bg-(--nav-action-frame-highlight)"></div>
		<div class="pixel-frame absolute inset-1 bg-(--nav-action-frame-shadow)"></div>
		<div class="pixel-frame absolute inset-1.5 bg-(--nav-action-frame-fill)"></div>
		<div class="pixel-frame absolute inset-1.5 flex items-center justify-center text-(--nav-action-frame-text)">
			<Icon icon="mdi:logout" class="size-4" />
		</div>
	</button>
{/if}

<style>
	@keyframes turn-flash {
		0% {
			transform: scale(0.6);
			opacity: 0;
		}
		35% {
			transform: scale(1.15);
			opacity: 1;
		}
		60% {
			transform: scale(1);
			opacity: 1;
		}
		100% {
			transform: scale(1);
			opacity: 1;
		}
	}
	.turn-flash {
		display: inline-block;
		animation: turn-flash 0.6s ease-out;
	}

	@media (prefers-reduced-motion: reduce) {
		.turn-flash {
			animation: none;
		}
	}
</style>
